import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { type FoundContact, loadOutreachState, type OutreachEnrollment, type OutreachState, watchOutreach } from '@/lib/outreach';
import { SENDABLE_EMAIL_STATUSES } from '@shared/outreach';

/**
 * The sequence page's data, live: contacts, the reader's own sequence and
 * prospects, the background work in progress. Gestures reload when they return;
 * the realtime channel brings what the worker does.
 */
export const useOutreach = (toolAppId: string) => {
  const [state, setState] = useState<OutreachState | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    const { data } = await supabase.auth.getUser();
    const id = data.user?.id ?? null;
    setUserId(id);
    if (!id) return;
    try {
      setState(await loadOutreachState(toolAppId, id));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'La séquence n’a pas pu être chargée.');
    }
  }, [toolAppId]);

  useEffect(() => {
    void reload();
    return watchOutreach(toolAppId, () => { void reload(); });
  }, [toolAppId, reload]);

  const derived = useMemo(() => {
    const contacts = state?.contacts ?? [];
    const contactsOf = (recordId: string): FoundContact[] => contacts.filter((contact) => contact.appRecordId === recordId && contact.state !== 'dismissed');
    /** The person a sequence goes to: the chosen ones, else the best ranked. */
    const primaryOf = (recordId: string): FoundContact | null => {
      const own = contactsOf(recordId);
      return own.find((contact) => contact.state === 'chosen') ?? own[0] ?? null;
    };
    const mine = (state?.enrollments ?? []).filter((enrollment) => enrollment.ownerUserId === userId);
    const enrollmentOf = (recordId: string): OutreachEnrollment | null => mine.find((enrollment) => enrollment.appRecordId === recordId) ?? null;
    const busy = (kind: string, subjectId: string) => (state?.jobs ?? []).some((job) => job.kind === kind && job.subjectId === subjectId && job.status !== 'failed');
    const failed = (kind: string, subjectId: string) => (state?.jobs ?? []).find((job) => job.kind === kind && job.subjectId === subjectId && job.status === 'failed') ?? null;
    const sendable = (contact: FoundContact | null) => Boolean(contact?.email && contact.emailStatus && SENDABLE_EMAIL_STATUSES.has(contact.emailStatus));
    return { contactsOf, primaryOf, enrollmentOf, mine, busy, failed, sendable };
  }, [state, userId]);

  return { state, userId, error, setError, reload, ...derived };
};

export type OutreachData = ReturnType<typeof useOutreach>;
